import "./personalData.css";
import React, { useState, useEffect } from "react";
import { Form, Row, Col, Button, Container } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useAppKeycloak } from "../../keycloak/keycloakUtils";
import { ServiceFactory } from "../../Api/Services/ServiceFactory";
import { ServiceNames } from "../../Api/registry/Services";
import { useToast } from "../../components/ToastContext/ToastContext";
import { useDispatch, useSelector } from 'react-redux';
import { fetchPerson } from '../../store/slices/personSlice';

const PersonalData = () => {
  const { t } = useTranslation(["usersetting", "error"]);
  const { showToast } = useToast();
  const [userService, setUserService] = useState(null);
  const [errors, setErrors] = useState({});
  const dispatch = useDispatch();
  const personData = useSelector((state) => state.person?.person);
  const loading = useSelector((state) => state.person?.loading);
  const error = useSelector((state) => state.person?.error);
  const userMeta = useSelector((state) => state.person?.userData);
  const userMetaLoading = useSelector((state) => state.person?.userDataLoading);
  const userMetaError = useSelector((state) => state.person?.userDataError);

  const [userData, setUserData] = useState({
    name: "",
    surname: "",
    email: "",
    username: "",
    phoneNumber: "",
    address: {
      country: "",
      city: "",
      zipCode: "",
      street: "",
      houseNumber: "",
      region: "",
    },
  });

  useEffect(() => {

    if (personData && userMeta) {
      setUserData({
        name: personData.name || "",
        surname: personData.surname || "",
        email: personData.email || "",
        username: userMeta?.userName || userMeta?.username || '',
        phoneNumber: personData.phoneNumber || "",
        address: {
          country: personData.address?.country || "",
          city: personData.address?.city || "",
          zipCode: personData.address?.zipCode || "",
          street: personData.address?.street || "",
          houseNumber: personData.address?.houseNumber || "",
          region: personData.address?.region || "",
        },
      });
    } else {
      console.log('PersonalData - personData is null or undefined');
    }
  }, [personData, userMeta]);

  useEffect(() => {
    const initializeService = async () => {
      const service = await ServiceFactory.getService(ServiceNames.UserService);
      setUserService(service);
    }
    initializeService();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: undefined,
    }));

    if (name.startsWith("address.")) {
      const addressField = name.split(".")[1];
      setUserData((prevData) => ({
        ...prevData,
        address: {
          ...prevData.address,
          [addressField]: value,
        },
      }));
    } else {
      setUserData((prevData) => ({
        ...prevData,
        [name]: value,
      }));
    }
  };

  const validateForm = (userData, t) => {
    const validationErrors = {};

    if (!userData.name) {
      validationErrors.name = t("usersetting:messages.namerequired");
    }
    if (!userData.surname) {
      validationErrors.surname = t("usersetting:messages.surnamerequired");
    }

    if (!userData.email) {
      validationErrors.email = t("usersetting:messages.emailinvalid");
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(userData.email)) {
      validationErrors.email = t("usersetting:messages.emailinvalid");
    }

    return validationErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validateForm(userData, t);

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      // Creiamo l'oggetto UpdatePersonDataRequest
      const updateRequest = {
        name: userData.name,
        surname: userData.surname,
        email: userData.email,
        phoneNumber: userData.phoneNumber || null,
        address: userData.address ? {
          country: userData.address.country || null,
          city: userData.address.city || null,
          street: userData.address.street || null,
          region: userData.address.region || null,
          houseNumber: userData.address.houseNumber || null,
          zipCode: userData.address.zipCode || null
        } : null
      };

      // Chiamiamo il metodo savePersonalData
      await userService.savePersonalData(updateRequest);
      showToast(t("usersetting:messages.success"), "success");

      // Ricarica i dati aggiornati nello store
      await dispatch(fetchPerson());

    } catch (error) {
      showToast(error.message || t("error:wrong"), "error");
    }
  };

  if (loading || userMetaLoading) {
    return <div>{t("usersetting:messages.loadingUserData")}</div>;
  }

  if (error || userMetaError) {
    return <div>{t("usersetting:messages.errorLoadingUserData")} {error}</div>;
  }

  return (
    <Container>
      <Form onSubmit={handleSubmit} noValidate>
        <Form.Group as={Row} className="mb-3">
          <Col sm={1}>
            <Form.Label column sm={2} style={{ textAlign: 'right', color: 'white' }}>
              {t("usersetting:caption.username")}
            </Form.Label>
          </Col>
          <Col sm={2}>
            <Form.Control
              type="text"
              name="username"
              value={userData.username || ""}
              readOnly
            />
          </Col>
          <Col sm={1}>
            <Form.Label column sm={2} style={{ textAlign: 'right', color: 'white' }}>
              {t("usersetting:caption.email")}
            </Form.Label>
          </Col>
          <Col sm={4}>
            <Form.Control
              type="email"
              name="email"
              value={userData.email || ""}
              placeholder={t("usersetting:caption.email")}
              onChange={handleChange}
              isInvalid={!!errors.email}
            />
            <Form.Control.Feedback type="invalid">
              {errors.email}
            </Form.Control.Feedback>
          </Col>
          <Col sm={1} style={{ width: 'fit-content' }}>
            <Form.Label column style={{ color: 'white' }}>
              {t("usersetting:caption.phonenumber")}
            </Form.Label>
          </Col>
          <Col sm={2}>
            <Form.Control
              type="tel"
              name="phoneNumber"
              value={userData.phoneNumber || ""}
              placeholder={t("usersetting:caption.phonenumber")}
              onChange={handleChange}
              isInvalid={!!errors.phoneNumber}
            />
            <Form.Control.Feedback type="invalid">
              {errors.phoneNumber}
            </Form.Control.Feedback>
          </Col>
        </Form.Group>

        <Form.Group as={Row} className="mb-3">
          <Col sm={1}>
            <Form.Label column style={{ color: 'white' }}>
              {t("usersetting:caption.name")}
            </Form.Label>
          </Col>
          <Col sm={3}>
            <Form.Control
              type="text"
              name="name"
              value={userData.name || ""}
              onChange={handleChange}
              placeholder={t("usersetting:caption.name")}
              isInvalid={!!errors.name}
            />
            <Form.Control.Feedback type="invalid">
              {errors.name}
            </Form.Control.Feedback>
          </Col>
          <Col sm={1}>
            <Form.Label column style={{ color: 'white' }}>
              {t("usersetting:caption.surname")}
            </Form.Label>
          </Col>
          <Col sm={3}>
            <Form.Control
              type="text"
              name="surname"
              value={userData.surname || ""}
              placeholder={t("usersetting:caption.surname")}
              onChange={handleChange}
              isInvalid={!!errors.surname}
            />
            <Form.Control.Feedback type="invalid">
              {errors.surname}
            </Form.Control.Feedback>
          </Col>
          <Col sm={1} style={{ width: 'fit-content' }}>
            <Form.Label column style={{ color: 'white' }}>
              {t("usersetting:caption.nation")}
            </Form.Label>
          </Col>
          <Col sm={2}>
            <Form.Control
              type="text"
              name="address.country"
              placeholder={t("usersetting:caption.nation")}
              value={userData.address?.country || ""}
              onChange={handleChange}
              isInvalid={!!errors.country}
            />
            <Form.Control.Feedback type="invalid">
              {errors.country}
            </Form.Control.Feedback>
          </Col>
        </Form.Group>

        <Form.Group as={Row} className="mb-3">
          <Col sm={1}>
            <Form.Label column style={{ color: 'white' }}>
              {t("usersetting:caption.address")}
            </Form.Label>
          </Col>
          <Col sm={5}>
            <Form.Control
              type="text"
              name="address.street"
              placeholder={t("usersetting:caption.address")}
              value={userData.address?.street || ""}
              onChange={handleChange}
              isInvalid={!!errors.street}
            />
            <Form.Control.Feedback type="invalid">
              {errors.street}
            </Form.Control.Feedback>
          </Col>
          <Col sm={1}>
            <Form.Label column style={{ color: 'white', margin: '0px 0px 10px 50px' }}>
              {t("usersetting:caption.housenumber")}
            </Form.Label>
          </Col>
          <Col sm={1}>
            <Form.Control
              type="text"
              name="address.houseNumber"
              placeholder={t("usersetting:caption.housenumber")}
              value={userData.address?.houseNumber || ""}
              onChange={handleChange}
              isInvalid={!!errors.houseNumber}
            />
            <Form.Control.Feedback type="invalid">
              {errors.houseNumber}
            </Form.Control.Feedback>
          </Col>
          <Col sm={3}></Col>
        </Form.Group>

        <Form.Group as={Row} className="mb-3">
          <Col sm={1}>
            <Form.Label column style={{ color: 'white' }}>
              {t("usersetting:caption.city")}
            </Form.Label>
          </Col>
          <Col sm={2}>
            <Form.Control
              type="text"
              name="address.city"
              placeholder={t("usersetting:caption.city")}
              value={userData.address?.city || ""}
              onChange={handleChange}
              isInvalid={!!errors.city}
            />
            <Form.Control.Feedback type="invalid">
              {errors.city}
            </Form.Control.Feedback>
          </Col>
          <Col sm={1} style={{ padding: '0px 0px 0px 12px' }}>
            <Form.Label column style={{ color: 'white', margin: '0px 0px 10px 0px' }}>
              {t("usersetting:caption.zipcode")}
            </Form.Label>
          </Col>
          <Col sm={2}>
            <Form.Control
              type="text"
              name="address.zipCode"
              placeholder={t("usersetting:caption.zipcode")}
              value={userData.address?.zipCode || ""}
              onChange={handleChange}
              isInvalid={!!errors.zipCode}
            />
            <Form.Control.Feedback type="invalid">
              {errors.zipCode}
            </Form.Control.Feedback>
          </Col>
        </Form.Group>

        <Row>
          <Col className="d-flex justify-content-end">
            <Button variant="primary" type="submit">
              {t("usersetting:button.caption.save")}
            </Button>
          </Col>
        </Row>
      </Form>
    </Container>
  );
};

export default PersonalData;